import type {
  BillChargeKind,
  BillFlag,
  BillShareLink,
  BillSlip,
  BillStatus,
  ChargeSplitMode,
} from "@boo/shared";

/** Owner is always the person with this display name. */
export const OWNER_NAME = "Me";

export interface EditorPerson {
  id: string;
  name: string;
  isOwner: boolean;
  /** Null only for the owner. */
  friendId: string | null;
}

export interface EditorItem {
  /** Client-side key; server item ids are regenerated on every save. */
  key: string;
  name: string;
  amount: number;
  quantity: number;
  claimedBy: string[];
}

export interface EditorCharge {
  key: string;
  label: string;
  kind: BillChargeKind;
  /** Signed; a discount is negative. */
  amount: number;
  splitMode: ChargeSplitMode;
  /** Editor-only: amount follows this % of the items until typed by hand. */
  percent?: number;
}

export interface BillEditor {
  billId: string | null;
  transactionId: string | null;
  status: BillStatus;
  /** Paid amount of the linked expense; null for a brand-new bill. */
  transactionAmount: number | null;
  title: string;
  /** YYYY-MM-DD */
  date: string;
  /** Everyone pays an equal share; item claims are ignored. */
  goDutch: boolean;
  people: EditorPerson[];
  items: EditorItem[];
  charges: EditorCharge[];
  /** Server persisted paid flag per person id (finalised bills). */
  paid: Record<string, boolean>;
  finalAmounts: Record<string, number | null>;
  /** Friend confirmed their current share (owner is always true). */
  reviewed: Record<string, boolean>;
  /** Every friend who owes something has confirmed. */
  readyToFinalize: boolean;
  /** How the owner appears to friends. */
  ownerDisplayName: string;
  paymentProfileId: string | null;
  hasReceipt: boolean;
  share: BillShareLink | null;
  flags: BillFlag[];
  slips: BillSlip[];
  /** Server revision this copy was loaded at; compared with live-update events. */
  revision: string;
  dirty: boolean;
}

export const INPUT_CLASS =
  "rounded-lg border border-border bg-transparent px-3 py-2.5 text-base sm:py-1.5 sm:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60";
